import { z } from "zod";

import http from "@/services/http";
import isUnauth from "@/utils/isUnauth";

const productPromotion = z
  .object({
    calloutMsg: z.string(),
    promotionId: z.string(),
    promotionalPrice: z.number(),
  })
  .loose();

export const productsPricing = z
  .object({
    productGuid: z.number(),
    name: z.string(),
    price: z.number(),
    guid: z.string(),
    is_single: z.boolean(),
    discount: z.number(),
    discount_price: z.number(),
    acronym: z.string(),
    version: z.string(),
    clc_count: z.number(),
    subscription_length_days: z.number(),
    content_bundle_id: z.array(z.number()),
    eosDate: z.iso.datetime().nullish(),
    product_promotions: z.array(productPromotion).nullish(),
  })
  .loose();

const contents = z
  .object({
    id: z.number(),
    guid: z.string(),
    name: z.string(),
    type: z.string(),
    url: z.string().nullish(),
    friendly_url: z.string().nullish(),
  })
  .strict();

const cataLogItem = z
  .object({
    id: z.number(),
    title: z.string().nullish(),
    name: z.string().nullish(),
    guid: z.string(),
    type: z.string(),
    friendly_url: z.string().nullish(),
    url: z.string().nullish(),
  })
  .loose();

const contentBundle = z
  .object({
    id: z.number(),
    name: z.string(),
    description: z.string(),
    guid: z.string(),
    is_single: z.number(),
    active: z.number(),
    content_count: z.number().nullish(),
    friendly_url: z.string().nullish(),
    root_content_count: z.number().nullish(),
    bundle_type_id: z.number().nullish(),
    bundle_type: z.string().nullish(),
    created_at: z.iso.datetime().nullish(),
    updated_at: z.iso.datetime().nullish(),
    deleted_at: z.iso.datetime().nullish(),
    contents: z.array(contents).nullish(),
    type: z.string().nullish().optional().default("bundle"),
    catalog_data: z.array(cataLogItem).nullish().optional(),
  })
  .loose();

export const contentBundleResponse = z.array(contentBundle);

export const productsPricingResponse = z.array(productsPricing);

export async function productPricing() {
  const { data } = await http().get(`${globalThis.env.API_CATALOG}/productsPricing`);

  return productsPricingResponse.parse(data);
}

export async function fetchById(Id: number) {
  const { data } = await http().post(`${globalThis.env.API_CATALOG}/get-content-catalog-bundle`, {
    content_bundle_id: [Id],
  });

  return contentBundleResponse.parse(data);
}

export async function fetchByGuids(guids: string[]) {
  if (guids.length === 0) return [];
  const body = {
    content_bundle_guid: guids,
  };

  const { data } = isUnauth()
    ? await http().post(`${globalThis.env.API_UNAUTH}/get-content-catalog-bundle`, body)
    : await http().post(`${globalThis.env.API_CATALOG}/get-root-content-catalog-bundle`, body);
  return contentBundleResponse.parse(data);
}

const rootBundle = z
  .object({
    id: z.number(),
    name: z.string(),
    guid: z.string(),
    root_content_count: z.number(),
    bundle_type: z.string().nullish(),
    catalog_data: z.array(cataLogItem).nullish(),
  })
  .loose();

export const rootContentBundle = z.array(rootBundle);

export type catalogItemTypes = z.infer<typeof cataLogItem>;
export type rootBundleTypes = z.infer<typeof rootBundle>;

export async function getRootContentBundles(bundlesIDs: any[], include_product: boolean) {
  // Check if all IDs are numbers or numeric strings that can be converted to integers
  const isNumericArray = bundlesIDs.every(
    (id) =>
      typeof id === "number" ||
      (typeof id === "string" && !Number.isNaN(Number(id)) && Number.isInteger(Number(id)))
  );

  const processedBundleIDs = isNumericArray ? bundlesIDs.map(Number) : bundlesIDs.map(String);

  const { data } = await http().post(
    `${globalThis.env.API_CATALOG}/get-root-content-catalog-bundle`,
    {
      content_bundle_id: processedBundleIDs,
      include_product,
    }
  );
  return rootContentBundle.parse(data);
}

// POST /GetProductContentBundles response is []dto.GetProductContentBundleResp; ContentBundle is
// a flattened dto.GetProductBundleResp (BundleTypeID/FriendlyURL are nullable pointers on the Go
// side - no omitempty - so they genuinely serialize as null).
const productBundleRoot = z
  .object({
    bundle_type_id: z.number().nullish(),
    friendly_url: z.string().nullish(),
    guid: z.string().nullish(),
    is_single: z.number().nullish(),
    root_contents: z
      .array(z.object({ type: z.string().nullish(), friendly_url: z.string().nullish() }).loose())
      .nullish(),
  })
  .loose();

const productContentBundle = z
  .object({
    id: z.number().nullish(),
    global_product_id: z.number(),
    global_product_name: z.string(),
    eos_date: z.string().nullish(),
    content_bundle: productBundleRoot,
  })
  .loose();

export const productContentBundlesResponse = z.array(productContentBundle);

export async function GetProductContentBundles(productIDs: (string | number)[]) {
  if (productIDs.length === 0) return [];
  const { data } = await http().post(`${globalThis.env.API_CATALOG}/GetProductContentBundles`, {
    productIds: productIDs,
  });
  return productContentBundlesResponse.parse(data);
}
